import { Controller } from "stimulus";
import { DirectUpload } from "@rails/activestorage";

// Bulletin editor:
//
//  - Two-column layout: markdown body on the left, image sidebar on
//    the right. Write / Split / Preview toolbar on the body card;
//    preview goes through the server so it renders exactly like the
//    show page will (Redcarpet + shot: resolution + figure/carousel
//    promotion) rather than a client-side markdown approximation.
//  - Auto-slug from the title until the author touches the slug.
//  - Word / character counts under the editor.
//  - Explicit dropzone: drop, paste on body, or click to file-pick.
//    Uploads go through Active Storage DirectUpload with a per-file
//    progress card, then either call POST /bulletins/:id/attach_image
//    (edit) or stash a hidden bulletin[image_signed_ids][] for the
//    controller to attach on create (new).
//  - Copy / Insert / Remove on every attached image row, plus a small
//    bottom-center toast for the "Copied shot:foo" / "Inserted"
//    confirmations.

export default class extends Controller {
  static targets = [
    "textarea", "imageList", "dropzone", "picker", "uploads",
    "title", "slug", "slugAuto",
    "editorBody", "preview", "counts",
    "toast", "shotsEmpty",
  ];
  static values = {
    attachUrl: String,
    detachUrl: String,
    previewUrl: String,
    recordId: Number,
    resourceName: String,
    slugLocked: Boolean,
  };

  connect() {
    this.pending = 0;
    this.mode = "write";
    this.slugTouched = this.slugLockedValue || (this.hasSlugTarget && this.slugTarget.value.trim().length > 0);
    this.stemUrls = this.collectExistingStemUrls();
    this.submitButtons = Array.from(this.element.querySelectorAll("input[type='submit'], button[type='submit']"));
    this.updateCounts();
    this.updateSlugAutoTag();
  }

  // --- Header / auto-slug -------------------------------------------------

  titleChanged() {
    if (this.slugTouched || !this.hasSlugTarget) return;
    this.slugTarget.value = slugify(this.titleTarget.value);
    this.updateSlugAutoTag();
  }

  slugChanged() {
    if (this.slugLockedValue) return;
    this.slugTouched = this.slugTarget.value.trim().length > 0;
    this.updateSlugAutoTag();
  }

  updateSlugAutoTag() {
    if (!this.hasSlugAutoTarget) return;
    const showing = !this.slugTouched && this.hasSlugTarget && this.slugTarget.value.length > 0;
    this.slugAutoTarget.classList.toggle("is-on", showing);
  }

  // --- Editor mode toolbar ------------------------------------------------

  setModeFromEvent(event) {
    const mode = event.currentTarget.dataset.mode;
    if (!mode) return;
    this.setMode(mode);
  }

  setMode(mode) {
    this.mode = mode;
    this.element.querySelectorAll(".bulletin-seg button[data-mode]").forEach((btn) => {
      btn.setAttribute("aria-pressed", String(btn.dataset.mode === mode));
    });
    if (!this.hasEditorBodyTarget) return;
    this.editorBodyTarget.classList.toggle("is-split", mode === "split");
    const writePane = this.editorBodyTarget.querySelector('[data-pane="write"]');
    const previewPane = this.editorBodyTarget.querySelector('[data-pane="preview"]');
    if (writePane) writePane.hidden = mode === "preview";
    if (previewPane) previewPane.hidden = mode === "write";
    if (mode !== "write") this.schedulePreview({ immediate: true });
  }

  // --- Body input: counts + debounced preview ----------------------------

  bodyChanged() {
    this.updateCounts();
    this.schedulePreview();
  }

  updateCounts() {
    if (!this.hasTextareaTarget || !this.hasCountsTarget) return;
    const text = this.textareaTarget.value;
    const trimmed = text.trim();
    const words = trimmed ? trimmed.split(/\s+/).length : 0;
    const chars = text.length;
    this.countsTarget.textContent = `${words} word${words === 1 ? "" : "s"} · ${chars} character${chars === 1 ? "" : "s"}`;
  }

  schedulePreview({ immediate = false } = {}) {
    if (this.mode === "write") return;
    if (!this.hasPreviewUrlValue || !this.previewUrlValue) return;

    if (this.previewTimer) clearTimeout(this.previewTimer);
    const delay = immediate ? 0 : 250;
    this.previewTimer = setTimeout(() => this.renderPreview(), delay);
  }

  async renderPreview() {
    if (!this.hasPreviewTarget || !this.hasTextareaTarget) return;
    const body = this.textareaTarget.value;

    if (body.trim().length === 0) {
      this.previewTarget.innerHTML = '<p class="bulletin-editor-preview-empty">Nothing to preview yet.</p>';
      return;
    }

    const pendingSignedIds = this.pendingSignedIds();
    const payload = {
      body_md: body,
      pending_signed_ids: pendingSignedIds,
    };
    if (this.hasRecordIdValue && this.recordIdValue) payload[`${this.resourceName()}_id`] = this.recordIdValue;

    const requestId = ++this.previewRequestId;

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/html",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify(payload),
      });
      if (requestId !== this.previewRequestId) return;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      this.previewTarget.innerHTML = html || '<p class="bulletin-editor-preview-empty">Nothing to preview yet.</p>';
    } catch (err) {
      if (requestId !== this.previewRequestId) return;
      this.previewTarget.innerHTML = `<p class="bulletin-editor-preview-empty is-err">Preview failed: ${escapeHtml(err.message)}</p>`;
    }
  }

  get previewRequestId() { return this._previewRequestId || 0; }
  set previewRequestId(v) { this._previewRequestId = v; }

  // --- Stem->URL map, seeded from server-rendered rows -------------------

  collectExistingStemUrls() {
    const map = new Map();
    this.element.querySelectorAll(".bulletin-shot").forEach((row) => {
      const stemEl = row.querySelector("[data-stem]");
      const imgEl = row.querySelector("img[src]");
      if (stemEl && imgEl) map.set(stemEl.dataset.stem, imgEl.getAttribute("src"));
    });
    return map;
  }

  pendingSignedIds() {
    return Array.from(this.element.querySelectorAll(`input[name='${this.resourceName()}[image_signed_ids][]']`))
      .map((el) => el.value)
      .filter(Boolean);
  }

  // --- Dropzone -----------------------------------------------------------

  openPicker(event) {
    if (event) {
      if (this.pickerTarget.contains(event.target)) return;
      if (event.target.closest("button")) return;
      event.preventDefault();
    }
    this.pickerTarget.click();
  }

  dropzoneKey(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.pickerTarget.click();
    }
  }

  pickerChanged(event) {
    const files = Array.from(event.target.files || []);
    this.consumeFiles(files);
    event.target.value = "";
  }

  dropzoneEnter(event) {
    if (!Array.from(event.dataTransfer?.types || []).includes("Files")) return;
    event.preventDefault();
    this.dropzoneTarget.classList.add("is-active");
  }

  dropzoneLeave(event) {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    this.dropzoneTarget.classList.remove("is-active");
  }

  dropzoneDrop(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove("is-active");
    this.consumeFiles(Array.from(event.dataTransfer?.files || []));
  }

  // --- Paste on textarea --------------------------------------------------

  pastedOnTextarea(event) {
    const items = Array.from(event.clipboardData?.items || []);
    const fromItems = items
      .filter((i) => i.kind === "file" && i.type.startsWith("image/"))
      .map((i) => i.getAsFile())
      .filter(Boolean);
    const fromFiles = Array.from(event.clipboardData?.files || []);
    const files = this.dedupe([...fromFiles, ...fromItems]);
    if (files.length === 0) return;
    event.preventDefault();
    this.consumeFiles(files);
  }

  // --- Copy / Insert on existing rows -------------------------------------

  copyStem(event) {
    const stem = event.currentTarget.dataset.stem;
    if (!stem) return;
    navigator.clipboard.writeText(`shot:${stem}`).then(
      () => this.notify(`Copied shot:${stem}`),
      () => this.notify("Copy failed", { error: true }),
    );
  }

  insertStem(event) {
    const stem = event.currentTarget.dataset.stem;
    if (!stem) return;
    this.insertMarkdownImage(stem);
    this.notify(`Inserted shot:${stem}`);
  }

  async removeStem(event) {
    const btn = event.currentTarget;
    const stem = btn.dataset.stem;
    const attachmentId = btn.dataset.attachmentId;
    if (!stem) return;

    const refCount = this.countBodyReferences(stem);
    const refWord = refCount === 1 ? "reference" : "references";
    const prompt = refCount > 0
      ? `Delete shot:${stem}?\n\n${refCount} ${refWord} in the body will be removed too.`
      : `Delete shot:${stem}?`;
    if (!window.confirm(prompt)) return;

    // Strip body references first so the server can persist the
    // resulting body_md atomically with the attachment purge. If the
    // server call fails, we roll the textarea back.
    const originalBody = this.textareaTarget ? this.textareaTarget.value : null;
    if (refCount > 0) this.removeBodyReferences(stem);
    const strippedBody = this.textareaTarget ? this.textareaTarget.value : null;

    if (attachmentId && this.hasDetachUrlValue && this.detachUrlValue) {
      try {
        const response = await fetch(this.detachUrlValue, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-CSRF-Token": this.csrfToken,
          },
          body: JSON.stringify({ attachment_id: attachmentId, body_md: strippedBody }),
        });
        if (!response.ok) {
          const detail = await safeText(response);
          throw new Error(`HTTP ${response.status}${detail ? `: ${detail}` : ""}`);
        }
      } catch (err) {
        if (originalBody !== null && this.textareaTarget) {
          this.textareaTarget.value = originalBody;
          this.textareaTarget.dispatchEvent(new Event("input", { bubbles: true }));
        }
        this.notify(`Delete failed: ${err.message}`, { error: true });
        return;
      }
    } else {
      // Pending / deferred upload: no persisted attachment to detach.
      // The hidden signed_id input lives inside the sidebar row, so
      // removing the row below also removes it.
    }

    this.stemUrls.delete(stem);
    btn.closest(".bulletin-shot")?.remove();
    this.updateShotsEmpty();
    this.notify(`Removed shot:${stem}`);
    this.schedulePreview();
  }

  countBodyReferences(stem) {
    if (!this.hasTextareaTarget) return 0;
    const escaped = escapeRegex(stem);
    const lineRegex = new RegExp(`^\\s*shot:${escaped}\\s*$`, "gm");
    const mdRegex = new RegExp(`!\\[[^\\]]*\\]\\(shot:${escaped}\\)`, "g");
    const text = this.textareaTarget.value;
    return (text.match(lineRegex)?.length || 0) + (text.match(mdRegex)?.length || 0);
  }

  removeBodyReferences(stem) {
    const ta = this.textareaTarget;
    const escaped = escapeRegex(stem);
    // End-of-line anchor is mandatory: without it, removing shot:cover
    // when the body also has shot:cover-2x eats "shot:cover" out of the
    // longer line and leaves a mangled "-2x" behind.
    const lineRegex = new RegExp(`^[ \\t]*shot:${escaped}[ \\t]*$\\n?`, "gm");
    const mdRegex = new RegExp(`!\\[[^\\]]*\\]\\(shot:${escaped}\\)`, "g");
    let text = ta.value.replace(lineRegex, "").replace(mdRegex, "");
    // Collapse the double-blank-line gaps a removal often leaves behind.
    text = text.replace(/\n{3,}/g, "\n\n");
    ta.value = text;
    ta.dispatchEvent(new Event("input", { bubbles: true }));
  }

  updateShotsEmpty() {
    if (!this.hasImageListTarget || !this.hasShotsEmptyTarget) return;
    const hasChildren = this.imageListTarget.querySelector(".bulletin-shot");
    this.shotsEmptyTarget.hidden = !!hasChildren;
  }

  // --- Upload pipeline ----------------------------------------------------

  consumeFiles(files) {
    const images = files.filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) {
      this.showUploadError("No image files detected. Only image/* is accepted.");
      return;
    }
    images.forEach((file) => this.uploadFile(file));
  }

  uploadFile(file) {
    const used = this.usedStems();
    const { name, stem } = uniqueNameFor(file.name, used);
    if (name !== file.name) {
      file = new File([file], name, { type: file.type, lastModified: file.lastModified });
    }

    const card = this.buildUploadCard(file);
    card.dataset.stem = stem;
    this.uploadsTarget.appendChild(card);
    this.startPending();

    const upload = new DirectUpload(file, "/rails/active_storage/direct_uploads", {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (evt) => {
          if (!evt.lengthComputable) return;
          const pct = Math.round((evt.loaded / evt.total) * 100);
          card.querySelector(".bulletin-upload-bar").style.width = `${pct}%`;
          card.querySelector(".bulletin-upload-pct").textContent = `${pct}%`;
        });
      },
    });

    upload.create(async (error, blob) => {
      try {
        if (error) {
          this.markCardError(card, `Upload failed: ${error}`);
          return;
        }

        if (this.attachUrlValue) {
          await this.attachToBulletin(card, blob);
        } else {
          const stem = stemFromFilename(file.name);
          const url = blobRedirectUrl(blob.signed_id, file.name);
          this.registerStem(stem, url);
          this.appendClientImageRow(
            { stem, filename: file.name, url },
            { signedId: blob.signed_id },
          );
          const inserted = this.autoInsertIfFocused(stem);
          this.markCardSuccess(card, { stem, filename: file.name, deferred: true, inserted });
          this.schedulePreview();
        }
      } finally {
        this.finishPending();
      }
    });
  }

  startPending() {
    this.pending += 1;
    this.updateSubmitState();
  }

  finishPending() {
    this.pending = Math.max(0, this.pending - 1);
    this.updateSubmitState();
  }

  updateSubmitState() {
    if (!this.submitButtons) return;
    const busy = this.pending > 0;
    // Only toggle `disabled` and a data-state hook — rewriting the label
    // to "Uploading…" wipes any embedded content (e.g. the send icon in
    // Publish bulletin) and doesn't restore it. CSS can style
    // [data-uploading="true"] if we want a visual cue later.
    this.submitButtons.forEach((btn) => {
      btn.disabled = busy;
      btn.dataset.uploading = busy ? "true" : "false";
    });
  }

  async attachToBulletin(card, blob) {
    try {
      const response = await fetch(this.attachUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({ signed_id: blob.signed_id }),
      });

      if (!response.ok) {
        const detail = await safeText(response);
        throw new Error(`HTTP ${response.status}${detail ? `: ${detail}` : ""}`);
      }

      const data = await response.json();
      this.registerStem(data.stem, data.url);
      this.appendServerImageRow(data);
      const inserted = this.autoInsertIfFocused(data.stem);
      this.markCardSuccess(card, { ...data, inserted });
      this.schedulePreview();
    } catch (err) {
      this.markCardError(card, `Attach failed: ${err.message}`);
    }
  }

  registerStem(stem, url) {
    this.stemUrls.set(stem, url);
    if (this.hasShotsEmptyTarget) this.shotsEmptyTarget.hidden = true;
  }

  buildUploadCard(file) {
    const card = document.createElement("div");
    card.className = "bulletin-upload-card is-uploading";
    card.innerHTML = `
      <div class="bulletin-upload-row">
        <span class="bulletin-upload-name">${escapeHtml(file.name)}</span>
        <span class="bulletin-upload-pct">0%</span>
      </div>
      <div class="bulletin-upload-track"><div class="bulletin-upload-bar"></div></div>
      <div class="bulletin-upload-result"></div>
    `;
    return card;
  }

  markCardSuccess(card, data) {
    card.classList.remove("is-uploading");
    card.classList.add("is-done");
    const bar = card.querySelector(".bulletin-upload-bar");
    if (bar) bar.style.width = "100%";
    const base = data.deferred ? `Uploaded. Will attach on save.` : `Attached.`;
    const tail = data.inserted ? `shot:${data.stem} inserted at cursor.` : `shot:${data.stem} ready to Insert.`;
    const message = `${base} ${tail}`;
    card.querySelector(".bulletin-upload-result").innerHTML = `
      <span class="bulletin-upload-status is-ok">${escapeHtml(message)}</span>
      <button type="button" class="bulletin-upload-dismiss" data-action="click->bulletin-editor#dismissCard">Dismiss</button>
    `;
    setTimeout(() => card.remove(), 4000);
  }

  markCardError(card, message) {
    card.classList.remove("is-uploading");
    card.classList.add("is-error");
    card.querySelector(".bulletin-upload-result").innerHTML = `
      <span class="bulletin-upload-status is-err">${escapeHtml(message)}</span>
      <button type="button" class="bulletin-upload-dismiss" data-action="click->bulletin-editor#dismissCard">Dismiss</button>
    `;
  }

  showUploadError(message) {
    const card = document.createElement("div");
    card.className = "bulletin-upload-card is-error";
    card.innerHTML = `
      <div class="bulletin-upload-result">
        <span class="bulletin-upload-status is-err">${escapeHtml(message)}</span>
        <button type="button" class="bulletin-upload-dismiss" data-action="click->bulletin-editor#dismissCard">Dismiss</button>
      </div>
    `;
    this.uploadsTarget?.appendChild(card);
  }

  dismissCard(event) {
    event.currentTarget.closest(".bulletin-upload-card")?.remove();
  }

  // --- Textarea splicing --------------------------------------------------

  autoInsertIfFocused(stem) {
    // Only splice a shot: reference into the body when the author is
    // actively writing (textarea has focus). Dropping / clicking the
    // picker leaves focus elsewhere; those flows just get the sidebar
    // row and the author clicks Insert when they want to reference it.
    if (document.activeElement !== this.textareaTarget) return false;
    this.insertMarkdownImage(stem);
    return true;
  }

  insertMarkdownImage(stem) {
    const ta = this.textareaTarget;
    ta.focus();
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    const before = ta.value.slice(0, start);
    const after = ta.value.slice(end);

    const needsBlankBefore = before.length > 0 && !/\n\n$/.test(before);
    const needsBlankAfter = after.length > 0 && !/^\n\n/.test(after);
    const padBefore = needsBlankBefore ? "\n\n" : "";
    const padAfter = needsBlankAfter ? "\n\n" : "\n";

    const opener = "![";
    const closer = `](shot:${stem})`;
    const insertion = padBefore + opener + closer + padAfter;

    ta.value = before + insertion + after;
    // Cursor lands between the [] so typing an alt is one keystroke away.
    const cursor = before.length + padBefore.length + opener.length;
    ta.selectionStart = ta.selectionEnd = cursor;
    ta.dispatchEvent(new Event("input", { bubbles: true }));
  }

  insertAtCursor(text) {
    const ta = this.textareaTarget;
    ta.focus();
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    const before = ta.value.slice(0, start);
    const after = ta.value.slice(end);

    const needsBlankBefore = before.length > 0 && !/\n\n$/.test(before);
    const needsBlankAfter = after.length > 0 && !/^\n\n/.test(after);
    const insertion = (needsBlankBefore ? "\n\n" : "") + text + (needsBlankAfter ? "\n\n" : "\n");

    ta.value = before + insertion + after;
    const pos = before.length + insertion.length;
    ta.selectionStart = ta.selectionEnd = pos;
    ta.dispatchEvent(new Event("input", { bubbles: true }));
  }

  // --- Image list rows ----------------------------------------------------

  appendServerImageRow(data) {
    this.appendClientImageRow(data, { attachmentId: data.id });
  }

  appendClientImageRow(data, { attachmentId = null, signedId = null } = {}) {
    if (!this.hasImageListTarget) return;
    const li = document.createElement("li");
    li.className = signedId ? "bulletin-shot bulletin-shot-pending" : "bulletin-shot";
    if (data.id) li.dataset.imageId = data.id;
    if (signedId) li.dataset.signedId = signedId;
    const attachmentAttr = attachmentId ? ` data-attachment-id="${escapeAttr(attachmentId)}"` : "";
    // Pending rows (no attachment id yet) carry the signed_id hidden
    // field inside the row so removing the row also removes the input.
    const hiddenSignedId = signedId
      ? `<input type="hidden" name="${escapeAttr(this.resourceName())}[image_signed_ids][]" value="${escapeAttr(signedId)}" data-filename="${escapeAttr(data.filename)}">`
      : "";
    const pendingTag = signedId
      ? ` <span class="bulletin-shot-pending-tag">pending</span>`
      : "";
    li.innerHTML = `
      ${hiddenSignedId}
      <img src="${escapeAttr(data.url)}" class="bulletin-shot-thumb" alt="">
      <div class="bulletin-shot-meta">
        <button type="button" class="bulletin-shot-ref"
          data-action="click->bulletin-editor#copyStem"
          data-stem="${escapeAttr(data.stem)}"
          title="Copy shot:${escapeAttr(data.stem)}">shot:${escapeHtml(data.stem)}</button>
        <div class="bulletin-shot-filename">${escapeHtml(data.filename)}${pendingTag}</div>
        <div class="bulletin-shot-actions">
          <button type="button" class="bulletin-mini bulletin-mini-primary"
            data-action="click->bulletin-editor#insertStem" data-stem="${escapeAttr(data.stem)}">Insert</button>
          <button type="button" class="bulletin-mini"
            data-action="click->bulletin-editor#copyStem" data-stem="${escapeAttr(data.stem)}">Copy ref</button>
          <button type="button" class="bulletin-mini bulletin-mini-ghost"
            data-action="click->bulletin-editor#removeStem"
            data-stem="${escapeAttr(data.stem)}"${attachmentAttr}>Remove</button>
        </div>
      </div>
    `;
    this.imageListTarget.appendChild(li);
    if (this.hasShotsEmptyTarget) this.shotsEmptyTarget.hidden = true;
  }

  // --- misc ---------------------------------------------------------------

  usedStems() {
    const stems = new Set();
    this.element.querySelectorAll("[data-stem]").forEach((el) => {
      const s = el.dataset.stem;
      if (s) stems.add(s);
    });
    // Hidden signed_id inputs now live inside their sidebar rows,
    // but also sweep any legacy detached inputs just in case.
    this.element.querySelectorAll("input[data-filename]").forEach((el) => {
      stems.add(stemFromFilename(el.dataset.filename));
    });
    this.stemUrls.forEach((_url, stem) => stems.add(stem));
    return stems;
  }

  dedupe(files) {
    const seen = new Set();
    return files.filter((f) => {
      const key = `${f.name}:${f.size}:${f.lastModified}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  notify(message, { error = false } = {}) {
    if (!this.hasToastTarget) return;
    const el = this.toastTarget;
    el.textContent = message;
    el.classList.toggle("is-error", !!error);
    el.classList.add("is-shown");
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => el.classList.remove("is-shown"), 1600);
  }

  get csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']");
    return meta ? meta.content : "";
  }

  resourceName() {
    return this.hasResourceNameValue && this.resourceNameValue ? this.resourceNameValue : "bulletin";
  }
}

function slugify(input) {
  return String(input || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stemFromFilename(filename) {
  return filename.replace(/\.[^.]+$/, "");
}

function uniqueNameFor(filename, usedStems) {
  const dot = filename.lastIndexOf(".");
  const rawStem = dot >= 0 ? filename.slice(0, dot) : filename;
  const ext = dot >= 0 ? filename.slice(dot).toLowerCase() : "";
  // Slugify unconditionally so the stem always survives the
  // `shot:<\S+>` regex on the show-page renderer. This also normalizes
  // uppercase, punctuation, and unicode marks.
  const baseStem = slugify(rawStem) || "image";
  if (!usedStems.has(baseStem)) return { name: baseStem + ext, stem: baseStem };
  let i = 2;
  while (usedStems.has(`${baseStem}-${i}`)) i++;
  const newStem = `${baseStem}-${i}`;
  return { name: newStem + ext, stem: newStem };
}

function blobRedirectUrl(signedId, filename) {
  return `/rails/active_storage/blobs/redirect/${signedId}/${encodeURIComponent(filename)}`;
}

async function safeText(response) {
  try { return (await response.text()).slice(0, 200); } catch { return ""; }
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
function escapeAttr(s) { return escapeHtml(s); }
